export class HelperService {

  // Private static instance of the class (singleton)
  private static instance: HelperService;
  
  // Private constructor to prevent direct instantiation
  private constructor() {
  }

  // Method to return the singleton instance
  public static getInstance(): HelperService {
    if (!HelperService.instance) {
      HelperService.instance = new HelperService();
    }
    return HelperService.instance;
  }

  /** 
   * TEXT HELPERS
  **/

  public toCamelCase = (str: string | null | undefined): string => {
    if (!str) return 'unknown';
    return str
      .toLowerCase()
      .split(' ')
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join('');
  };

  /**
   * BBCODE HELPERS
  **/

  // Zet bbcode-markup veilig om naar HTML. HTML wordt eerst geëscaped zodat er
  // geen ongewenste tags of scripts in de output belanden; daarna worden de
  // ondersteunde bbcode-tags omgezet en newlines behouden als <br/>.
  public bbCodeToHtml = (input?: string | null): string => {
    if (!input) return '';

    let html = input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    html = html
      .replace(/\[b\]([\s\S]*?)\[\/b\]/gi, '<strong>$1</strong>')
      .replace(/\[i\]([\s\S]*?)\[\/i\]/gi, '<em>$1</em>')
      .replace(/\[u\]([\s\S]*?)\[\/u\]/gi, '<u>$1</u>')
      .replace(/\[s\]([\s\S]*?)\[\/s\]/gi, '<s>$1</s>');

    html = html
      .replace(/\[olist\]([\s\S]*?)\[\/olist\]/gi, (_match, content: string) =>
        this.renderOrderedList(content),
      )
      .replace(/\[list\]([\s\S]*?)\[\/list\]/gi, (_match, content: string) =>
        this.renderUnorderedList(content),
      );

    // [url=https://...]tekst[/url]
    html = html.replace(
      /\[url=(?:&quot;|')?([^\]"']+?)(?:&quot;|')?\]([\s\S]*?)\[\/url\]/gi,
      (_match, url: string, text: string) =>
        `<a href="${this.sanitizeUrl(url)}" target="_blank" rel="noopener noreferrer">${text}</a>`,
    );

    // [url]https://...[/url]
    html = html.replace(
      /\[url\]([\s\S]*?)\[\/url\]/gi,
      (_match, url: string) =>
        `<a href="${this.sanitizeUrl(url)}" target="_blank" rel="noopener noreferrer">${url}</a>`,
    );

    // [button=https://...]Tekst[/button] en [button url=https://...]Tekst[/button]
    html = html
      .replace(
        /\[button=(?:&quot;|')?([^\]"']+?)(?:&quot;|')?\]([\s\S]*?)\[\/button\]/gi,
        (_match, url: string, text: string) => this.renderButton(url, text),
      )
      .replace(
        /\[button\s+url=(?:&quot;|')?([^\]"']+?)(?:&quot;|')?\]([\s\S]*?)\[\/button\]/gi,
        (_match, url: string, text: string) => this.renderButton(url, text),
      )
      .replace(
        /\[button\]([\s\S]*?)\[\/button\]/gi,
        (_match, content: string) => this.renderInlineButton(content),
      );

    return html.replace(/\r\n|\r|\n/g, '<br/>');
  };

  private renderOrderedList = (content: string): string => {
    const finalItems = this.extractListItems(content);
    if (finalItems.length === 0) {
      return '';
    }

    return `<ol>${finalItems.map((item) => `<li>${item}</li>`).join('')}</ol>`;
  };

  private renderUnorderedList = (content: string): string => {
    const finalItems = this.extractListItems(content);
    if (finalItems.length === 0) {
      return '';
    }

    return `<ul>${finalItems.map((item) => `<li>${item}</li>`).join('')}</ul>`;
  };

  private extractListItems = (content: string): string[] => {
    const liMatches = Array.from(content.matchAll(/\[li\]([\s\S]*?)\[\/li\]/gi)).map((match) => match[1].trim());
    const starItems = content
      .split(/\[\*\]/gi)
      .slice(1)
      .map((item) => item.replace(/\[\/\*\]/gi, '').trim())
      .filter(Boolean);

    const items = liMatches.length > 0 ? liMatches : starItems;
    const fallback = content
      .split(/\r\n|\r|\n/g)
      .map((line) => line.trim())
      .filter(Boolean);

    const finalItems = (items.length > 0 ? items : fallback)
      .map((item) => item.replace(/\[\/?li\]/gi, '').trim())
      .filter(Boolean);

    return finalItems;
  };

  private renderButton = (url: string, text: string): string => {
    const href = this.sanitizeUrl(url);
    const label = (text ?? '').trim() || 'Openen';
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="bbcode-button">${label}</a>`;
  };

  private renderInlineButton = (content: string): string => {
    const trimmed = (content ?? '').trim();
    const parts = trimmed.split('|').map((part) => part.trim()).filter(Boolean);

    if (parts.length === 2) {
      const [first, second] = parts;
      if (this.isLikelyUrl(first)) {
        return this.renderButton(first, second);
      }
      if (this.isLikelyUrl(second)) {
        return this.renderButton(second, first);
      }
    }

    if (this.isLikelyUrl(trimmed)) {
      return this.renderButton(trimmed, 'Openen');
    }

    return `<span>${trimmed}</span>`;
  };

  private isLikelyUrl = (value: string): boolean => /^(https?:\/\/|www\.|mailto:)/i.test((value ?? '').trim());

  // Laat alleen veilige http(s)/mailto-links door en blokkeert o.a. javascript:.
  private sanitizeUrl = (url: string): string => {
    const trimmed = (url ?? '').trim();
    if (/^(https?:|mailto:)/i.test(trimmed)) return trimmed;
    if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
    if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return '#';
    return trimmed;
  };


  /** 
   * USER HELPERS
  **/

  public getAvatarLetters = (username: string,) => {
    // TODO getInitials from full name when available search in this app
    // const [firstName, lastName] = email.split(' ');
    // `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
    const result = username.charAt(0).toUpperCase();
    return result;
  };

  public getUserColor = (username: string = '?') => {
    const colors = ['#3F51B5', '#2196F3', '#009688', '#FF5722', '#795548', '#673AB7', '#E91E63', '#4CAF50', '#FF9800', '#9C27B0'];
    const hash = username.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };


  /** 
   * DATE HELPERS
  **/

  public formatDutchDate = (dateString?: string | null) => {
    if (!dateString) return '';
    return new Intl.DateTimeFormat('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(new Date(dateString));
  };
};

// Export an instance of the navigationService class
export const helperService = HelperService.getInstance();
